import { useCallback, useEffect, useState } from "react";
import { PRESETS } from "@/lib/presets";
import { MAX_BODY_LENGTH, MAX_CHANNELS_PER_SUBSCRIPTION, MAX_TITLE_LENGTH } from "@/lib/constants";
import {
  AlertIcon,
  BellIcon,
  BellOffIcon,
  BoltIcon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CloseIcon,
  DownloadIcon,
  ExitIcon,
  EyeIcon,
  EyeOffIcon,
  InfoIcon,
  LockIcon,
  MoonIcon,
  PhoneIcon,
  PlusIcon,
  SendIcon,
  ShieldIcon,
  SunIcon,
  SparkIcon,
  UserMinusIcon,
  UsersIcon,
} from "./icons";
import InstallGuide from "./install-guide";
import { dismissSplash } from "./splash";

type RuntimeConfig = {
  sendEnabled: boolean;
  vapidPublicKey: string | null;
};

type Channel = { id: string; name: string; memberCount: number };
type ChannelDevice = { id: string; label: string; joinedAt: string; lastSeenAt: string; isCurrent: boolean };
type NoticeType = "success" | "error" | "info";
type Notice = { type: NoticeType; text: string } | null;
type TabId = "status" | "rooms" | "presets";

declare global {
  interface Window {
    MSStream?: unknown;
  }
}

const TABS: { id: TabId; label: string; icon: typeof BellIcon }[] = [
  { id: "status", label: "สถานะ", icon: ShieldIcon },
  { id: "rooms", label: "ห้อง", icon: UsersIcon },
  { id: "presets", label: "ทดสอบ", icon: BoltIcon },
];

/**
 * Rooms stay unlocked across launches: joining or unlocking a room keeps its
 * secret in localStorage so sending inside the room is one tap. sessionStorage
 * would be the tighter choice in a browser tab, but a home-screen app closes
 * every time it is put away, and re-typing the room secret on each launch is
 * the same lost-data complaint as losing the room itself. Leaving the room
 * clears it, and the server still verifies the secret on every send.
 */
const SECRET_STORE_PREFIX = "room-secret:";

/**
 * The signed installation token, mirrored from the subscribe response. The
 * server sets the same value as an httpOnly cookie, but an iOS home-screen app
 * can relaunch without it; replaying this header restores the device's identity
 * instead of stranding its rooms on an installation nobody can reach.
 */
const INSTALLATION_TOKEN_KEY = "installation-token";

function readStored(key: string) {
  try {
    return localStorage.getItem(key) || "";
  } catch {
    return "";
  }
}

function writeStored(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private mode: the value simply does not persist */
  }
}

function forgetStored(key: string) {
  try {
    localStorage.removeItem(key);
  } catch {
    /* nothing stored to clear */
  }
}

function readRoomSecret(channelId: string) {
  return readStored(SECRET_STORE_PREFIX + channelId);
}

function writeRoomSecret(channelId: string, secret: string) {
  writeStored(SECRET_STORE_PREFIX + channelId, secret);
}

function forgetRoomSecret(channelId: string) {
  forgetStored(SECRET_STORE_PREFIX + channelId);
}

/** Keeps the server's error code, so behaviour never hinges on Thai copy. */
class ApiError extends Error {
  constructor(message: string, public code: string) {
    super(message);
  }
}

function apiError(payload: unknown) {
  const fallback = new ApiError("คำขอไม่สำเร็จ", "UNKNOWN");
  if (typeof payload !== "object" || payload === null || !("error" in payload)) return fallback;
  const error = payload.error;
  if (typeof error !== "object" || error === null) return fallback;
  const message = "message" in error && typeof error.message === "string" ? error.message : fallback.message;
  const code = "code" in error && typeof error.code === "string" ? error.code : fallback.code;
  return new ApiError(message, code);
}

function formatDateTime(value: string) {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime())
    ? "ไม่ทราบเวลา"
    : parsed.toLocaleString("th-TH", { dateStyle: "medium", timeStyle: "short" });
}

async function requestApi<T>(url: string, init: RequestInit = {}) {
  const token = readStored(INSTALLATION_TOKEN_KEY);
  // The shell now paints from the cache, so an offline launch reaches this
  // call and its raw "Failed to fetch" would land on screen as the app's own
  // wording. Say what actually happened instead.
  let response: Response;
  try {
    response = await fetch(url, {
      ...init,
      headers: {
        "content-type": "application/json",
        ...(token ? { "x-installation": token } : {}),
        ...(init.headers || {}),
      },
    });
  } catch {
    throw new ApiError("เชื่อมต่อเซิร์ฟเวอร์ไม่ได้ ตรวจสอบอินเทอร์เน็ตแล้วลองใหม่อีกครั้ง", "OFFLINE");
  }
  const payload: unknown = await response.json().catch(() => null);
  if (!response.ok) throw apiError(payload);
  return payload as T;
}

function decodeVapidKey(value: string) {
  const base64 = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
  const raw = atob(padded);
  return Uint8Array.from(raw, (char) => char.charCodeAt(0));
}

function createIdempotencyKey() {
  return crypto.randomUUID();
}

function Tile({ label, value, tone }: { label: string; value: string; tone: "on" | "off" | "idle" }) {
  return (
    <div className="tile">
      <span className="tile__label">{label}</span>
      <span className="tile__value">
        <span className={`dot dot--${tone}`} />
        {value}
      </span>
    </div>
  );
}

function Banner({ tone = "info", children }: { tone?: "info" | "warn" | "danger"; children: React.ReactNode }) {
  const Icon = tone === "info" ? InfoIcon : AlertIcon;
  return (
    <p className={`banner${tone === "info" ? "" : ` banner--${tone}`}`}>
      <Icon size={18} className="banner__icon" />
      <span>{children}</span>
    </p>
  );
}

function EmptyState({ icon, title, text, action }: { icon: React.ReactNode; title: string; text: string; action?: React.ReactNode }) {
  return (
    <div className="empty">
      <span className="empty__icon">{icon}</span>
      <span className="empty__title">{title}</span>
      <span>{text}</span>
      {action}
    </div>
  );
}

function ThemeToggle() {
  // The inline script in index.html already stamped <html data-theme>.
  const [theme, setTheme] = useState<"light" | "dark">(
    () => (document.documentElement.dataset.theme === "dark" ? "dark" : "light"),
  );

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute("content", theme === "dark" ? "#0a0a0a" : "#f5f5f5");
    try {
      localStorage.setItem("theme", theme);
    } catch {
      // private mode — the choice just does not survive a reload
    }
  }, [theme]);

  const dark = theme === "dark";
  return (
    <button
      type="button"
      className="theme-toggle"
      aria-label={dark ? "ใช้ธีมสว่าง" : "ใช้ธีมมืด"}
      aria-pressed={dark}
      onClick={() => setTheme(dark ? "light" : "dark")}
    >
      {dark ? <SunIcon size={18} /> : <MoonIcon size={18} />}
    </button>
  );
}

function SecretField({
  id,
  label,
  hint,
  value,
  onChange,
  placeholder,
  enterKeyHint,
}: {
  id: string;
  label: string;
  hint?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  enterKeyHint?: "done" | "go";
}) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="field">
      <span className="field__top"><label className="field__label" htmlFor={id}>{label}</label></span>
      <span className="control">
        <input
          id={id}
          className="input input--with-end"
          type={visible ? "text" : "password"}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          maxLength={64}
          autoComplete="off"
          enterKeyHint={enterKeyHint}
        />
        <button
          type="button"
          className="btn btn--icon control__end"
          onClick={() => setVisible((current) => !current)}
          aria-label={visible ? "ซ่อนรหัสห้อง" : "แสดงรหัสห้อง"}
        >
          {visible ? <EyeOffIcon size={18} /> : <EyeIcon size={18} />}
        </button>
      </span>
      {hint && <span className="field__hint">{hint}</span>}
    </div>
  );
}

export default function DemoClient() {
  const [tab, setTab] = useState<TabId>("status");
  const [config, setConfig] = useState<RuntimeConfig | null>(null);
  const [supported, setSupported] = useState(false);
  const [standalone, setStandalone] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission | "unsupported">("default");
  const [subscriptionId, setSubscriptionId] = useState<string | null>(null);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [openRoomId, setOpenRoomId] = useState<string | null>(null);
  const [devices, setDevices] = useState<ChannelDevice[]>([]);
  const [joinOpen, setJoinOpen] = useState(false);
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [notice, setNotice] = useState<Notice>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [joinName, setJoinName] = useState("");
  const [joinSecret, setJoinSecret] = useState("");
  const [unlockSecret, setUnlockSecret] = useState("");
  const [roomSecret, setRoomSecret] = useState("");
  const [customTitle, setCustomTitle] = useState("");
  const [customBody, setCustomBody] = useState("");

  const setInfo = useCallback((type: NoticeType, text: string) => setNotice({ type, text }), []);

  const syncSubscription = useCallback(async (subscription: PushSubscription) => {
    const result = await requestApi<{ subscriptionId: string; installationToken: string; channels: Channel[] }>("/api/push/subscriptions", {
      method: "POST",
      body: JSON.stringify({ subscription: subscription.toJSON() }),
    });
    writeStored(INSTALLATION_TOKEN_KEY, result.installationToken);
    setSubscriptionId(result.subscriptionId);
    setChannels(result.channels);
    return result;
  }, []);

  useEffect(() => {
    // The shell is on screen and usable now; the boot splash has done its job,
    // and the remaining status calls fill in behind it rather than blocking.
    dismissSplash();

    let cancelled = false;
    const beforeInstall = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as BeforeInstallPromptEvent);
    };
    const afterInstall = () => setInstalled(true);
    window.addEventListener("beforeinstallprompt", beforeInstall);
    window.addEventListener("appinstalled", afterInstall);

    const load = async () => {
      const pwaSupported = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
      if (cancelled) return;
      setSupported(pwaSupported);
      setStandalone(window.matchMedia("(display-mode: standalone)").matches || Boolean((navigator as Navigator & { standalone?: boolean }).standalone));
      setPermission("Notification" in window ? Notification.permission : "unsupported");
      try {
        const runtimeConfig = await requestApi<RuntimeConfig>("/api/runtime-config", { cache: "no-store" });
        if (cancelled) return;
        setConfig(runtimeConfig);
        if (!pwaSupported) return;
        const registration = await navigator.serviceWorker.register("/sw.js");
        const current = await registration.pushManager.getSubscription();
        if (current && !cancelled) await syncSubscription(current);
      } catch (error) {
        if (!cancelled) setInfo("error", error instanceof Error ? error.message : "โหลดสถานะระบบไม่สำเร็จ");
      }
    };
    void load();
    return () => {
      cancelled = true;
      window.removeEventListener("beforeinstallprompt", beforeInstall);
      window.removeEventListener("appinstalled", afterInstall);
    };
  }, [setInfo, syncSubscription]);

  useEffect(() => {
    if (!notice || notice.type === "error") return;
    const timer = window.setTimeout(() => setNotice(null), 6000);
    return () => window.clearTimeout(timer);
  }, [notice]);

  const refreshChannels = useCallback(async () => {
    if (!subscriptionId) return;
    try {
      const result = await requestApi<{ channels: Channel[] }>(`/api/subscriptions/${subscriptionId}/channels`, { cache: "no-store" });
      setChannels(result.channels);
    } catch {
      /* keep the cached list; the next action reports any real problem */
    }
  }, [subscriptionId]);

  /**
   * Another device in the room removed this one. Nothing is broken, so say so
   * plainly and drop everything the room left behind — the cached secret
   * included, since getting back in means entering the room name and secret
   * again rather than finding the room still half-open.
   */
  const handleRemovedFromRoom = useCallback((channelId: string) => {
    forgetRoomSecret(channelId);
    setChannels((current) => current.filter((channel) => channel.id !== channelId));
    setOpenRoomId(null);
    setRoomSecret("");
    setUnlockSecret("");
    setDevices([]);
    setInfo("error", "เครื่องนี้ถูกนำออกจากห้องแล้ว ต้องเข้าห้องใหม่ด้วยชื่อห้องและรหัสห้อง");
  }, [setInfo]);

  const refreshDevices = useCallback(async (channelId: string) => {
    if (!subscriptionId) return;
    try {
      const result = await requestApi<{ devices: ChannelDevice[] }>(
        `/api/channels/${channelId}/devices?subscriptionId=${subscriptionId}`,
        { cache: "no-store" },
      );
      setDevices(result.devices);
    } catch (error) {
      // The member list is the one call that reports a lost membership, so a
      // rejection here is how a removed device finds out it was removed.
      if (error instanceof ApiError && error.code === "CHANNEL_MEMBERSHIP_REQUIRED") {
        handleRemovedFromRoom(channelId);
        return;
      }
      /* the room still works without its member list */
      setDevices([]);
    }
  }, [subscriptionId, handleRemovedFromRoom]);

  /**
   * A removal happens on someone else's screen, so an open room re-checks
   * itself on a timer and whenever it comes back to the foreground; otherwise
   * a removed device would sit on a room screen it no longer belongs to.
   */
  useEffect(() => {
    if (!openRoomId || !subscriptionId) return;
    const poll = () => {
      void refreshChannels();
      void refreshDevices(openRoomId);
    };
    const timer = window.setInterval(poll, 20_000);
    const onVisibility = () => {
      if (document.visibilityState === "visible") poll();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [openRoomId, subscriptionId, refreshChannels, refreshDevices]);

  const install = async () => {
    // Safari never fires `beforeinstallprompt`, and Chrome withholds it until
    // its own heuristics are happy, so the illustrated steps are the fallback.
    if (!installPrompt) {
      setGuideOpen(true);
      return;
    }
    await installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    setInstallPrompt(null);
    // The tab that ran the prompt stays a plain tab, so `standalone` will not
    // flip here; the accepted choice is what retires the install box.
    if (outcome === "accepted") setInstalled(true);
  };

  const enableNotifications = async () => {
    if (!supported || !config?.vapidPublicKey) {
      setInfo("error", "เบราว์เซอร์นี้ยังไม่พร้อม หรือยังไม่ได้ตั้งค่า VAPID public key");
      return;
    }
    setBusy("enable");
    try {
      const nextPermission = await Notification.requestPermission();
      setPermission(nextPermission);
      if (nextPermission !== "granted") throw new Error("ผู้ใช้ยังไม่อนุญาต notification");
      const registration = await navigator.serviceWorker.ready;
      const current = await registration.pushManager.getSubscription();
      const next = current || await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: decodeVapidKey(config.vapidPublicKey),
      });
      await syncSubscription(next);
      setInfo("success", "เปิดรับ notification และบันทึก subscription แล้ว");
    } catch (error) {
      setInfo("error", error instanceof Error ? error.message : "เปิดรับ notification ไม่สำเร็จ");
    } finally {
      setBusy(null);
    }
  };

  const forceUpdate = async () => {
    setBusy("update");
    try {
      // ponytail: wipe the caches and re-fetch sw.js instead of tracking versions.
      // The registration itself stays: unregistering would take the push
      // subscription down with it and force the user to re-enable notifications.
      if ("caches" in window) await Promise.all((await caches.keys()).map((key) => caches.delete(key)));
      if ("serviceWorker" in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map((registration) => registration.update()));
      }
    } catch {
      // A blocked cache or service worker API still leaves the reload worth doing.
    }
    // Query string defeats the shell copy iOS keeps holding on to past no-cache.
    location.replace(`/?fresh=${Date.now()}`);
  };

  const disableNotifications = async () => {
    setBusy("disable");
    try {
      const registration = await navigator.serviceWorker.ready;
      const current = await registration.pushManager.getSubscription();
      if (current) await current.unsubscribe();
      if (subscriptionId) await requestApi(`/api/push/subscriptions/${subscriptionId}`, { method: "DELETE", body: "{}" });
      for (const channel of channels) forgetRoomSecret(channel.id);
      setSubscriptionId(null);
      setChannels([]);
      setOpenRoomId(null);
      setInfo("success", "ยกเลิก subscription แล้ว");
    } catch (error) {
      setInfo("error", error instanceof Error ? error.message : "ยกเลิก subscription ไม่สำเร็จ");
    } finally {
      setBusy(null);
    }
  };

  const openRoom = (channel: Channel) => {
    setOpenRoomId(channel.id);
    setRoomSecret(readRoomSecret(channel.id));
    setUnlockSecret("");
    setCustomTitle("");
    setCustomBody("");
    setDevices([]);
    void refreshChannels();
    void refreshDevices(channel.id);
  };

  const closeRoom = () => {
    setOpenRoomId(null);
    setRoomSecret("");
    setUnlockSecret("");
    setDevices([]);
    void refreshChannels();
  };

  const join = async () => {
    if (!subscriptionId) return setInfo("error", "เปิดรับ notification ก่อนเข้าห้อง");
    setBusy("join");
    try {
      const result = await requestApi<{ channel: { id: string; name: string }; channels: Channel[] }>("/api/channels/join", {
        method: "POST",
        body: JSON.stringify({ subscriptionId, name: joinName, secret: joinSecret }),
      });
      setChannels(result.channels);
      writeRoomSecret(result.channel.id, joinSecret);
      const joined = result.channels.find((channel) => channel.id === result.channel.id)
        || { ...result.channel, memberCount: 1 };
      setJoinName("");
      setJoinSecret("");
      setJoinOpen(false);
      setInfo("success", `เข้าห้อง ${joined.name} แล้ว`);
      openRoom(joined);
    } catch (error) {
      setInfo("error", error instanceof Error ? error.message : "เข้าห้องไม่สำเร็จ");
    } finally {
      setBusy(null);
    }
  };

  const leave = async (channelId: string) => {
    if (!subscriptionId) return;
    setBusy(`leave-${channelId}`);
    try {
      const result = await requestApi<{ channels: Channel[] }>(`/api/channels/${channelId}/memberships/${subscriptionId}`, { method: "DELETE", body: "{}" });
      forgetRoomSecret(channelId);
      setChannels(result.channels);
      if (openRoomId === channelId) {
        setOpenRoomId(null);
        setRoomSecret("");
      }
      setInfo("success", "ออกจากห้องแล้ว");
    } catch (error) {
      setInfo("error", error instanceof Error ? error.message : "ออกจากห้องไม่สำเร็จ");
    } finally {
      setBusy(null);
    }
  };

  const unlockRoom = (channelId: string) => {
    writeRoomSecret(channelId, unlockSecret);
    setRoomSecret(unlockSecret);
    setUnlockSecret("");
    setInfo("info", "ปลดล็อกห้องแล้ว รหัสจะถูกตรวจสอบอีกครั้งตอนส่ง");
  };

  const lockRoom = (channelId: string) => {
    forgetRoomSecret(channelId);
    setRoomSecret("");
    setInfo("info", "ล็อกห้องแล้ว ต้องใส่รหัสห้องอีกครั้งก่อนส่ง");
  };

  const removeDevice = async (channelId: string, device: ChannelDevice) => {
    if (!subscriptionId || device.isCurrent) return;
    if (!roomSecret) return setInfo("error", "ปลดล็อกห้องด้วยรหัสห้องก่อน จึงจะนำอุปกรณ์ออกได้");
    const confirmed = window.confirm(`นำ "${device.label}" ออกจากห้องนี้?\nเครื่องนั้นจะไม่ได้รับข้อความอีก และต้องเข้าห้องใหม่ด้วยชื่อห้องและรหัสห้อง`);
    if (!confirmed) return;

    setBusy(`remove-${device.id}`);
    try {
      const result = await requestApi<{ devices: ChannelDevice[] }>(`/api/channels/${channelId}/removals`, {
        method: "POST",
        body: JSON.stringify({ subscriptionId, targetSubscriptionId: device.id, secret: roomSecret }),
      });
      setDevices(result.devices);
      void refreshChannels();
      setInfo("success", `นำ ${device.label} ออกจากห้องแล้ว เครื่องนั้นต้องเข้าห้องใหม่`);
    } catch (error) {
      // Same as sending: a rejected secret is worth re-asking for.
      if (error instanceof ApiError && error.code === "INVALID_CHANNEL_SECRET") lockRoom(channelId);
      if (error instanceof ApiError && error.code === "DEVICE_NOT_IN_CHANNEL") void refreshDevices(channelId);
      setInfo("error", error instanceof Error ? error.message : "นำอุปกรณ์ออกไม่สำเร็จ");
    } finally {
      setBusy(null);
    }
  };

  const sendSelf = async (presetId: string) => {
    if (!subscriptionId || !config?.sendEnabled) return;
    setBusy(`preset-${presetId}`);
    try {
      const result = await requestApi<{ targeted: number; accepted: number; invalidated: number; failed: number }>("/api/notifications/self", {
        method: "POST",
        body: JSON.stringify({ subscriptionId, presetId, idempotencyKey: createIdempotencyKey() }),
      });
      setInfo(result.accepted ? "success" : "error", `ส่งเข้าเครื่องนี้แล้ว: provider รับ ${result.accepted}/${result.targeted} รายการ`);
    } catch (error) {
      setInfo("error", error instanceof Error ? error.message : "ส่งข้อความไม่สำเร็จ");
    } finally {
      setBusy(null);
    }
  };

  const sendRoom = async (channelId: string) => {
    if (!subscriptionId || !roomSecret || !config?.sendEnabled) return;
    setBusy("room-send");
    try {
      const result = await requestApi<{ targeted: number; accepted: number; invalidated: number; failed: number }>("/api/notifications/channel", {
        method: "POST",
        body: JSON.stringify({
          subscriptionId,
          channelId,
          secret: roomSecret,
          title: customTitle,
          body: customBody,
          idempotencyKey: createIdempotencyKey(),
        }),
      });
      setInfo(result.accepted ? "success" : "error", `ส่งถึงทุกอุปกรณ์ในห้องแล้ว: provider รับ ${result.accepted}/${result.targeted} เครื่อง`);
      if (result.accepted) {
        setCustomTitle("");
        setCustomBody("");
      }
      void refreshChannels();
    } catch (error) {
      // A rejected secret is the one error worth re-asking for, so drop it.
      if (error instanceof ApiError && error.code === "INVALID_CHANNEL_SECRET") lockRoom(channelId);
      setInfo("error", error instanceof Error ? error.message : "ส่งเข้าห้องไม่สำเร็จ");
    } finally {
      setBusy(null);
    }
  };

  const active = Boolean(subscriptionId);
  // Running from the home screen proves it; on Chrome the still-open tab never
  // becomes standalone, so an accepted prompt counts too.
  const appInstalled = standalone || installed;
  const sendingDisabled = !config?.sendEnabled || !active;
  const currentRoom = channels.find((channel) => channel.id === openRoomId) || null;
  const NoticeIcon = notice?.type === "success" ? CheckIcon : notice?.type === "error" ? AlertIcon : InfoIcon;

  const needsSubscription = (
    <EmptyState
      icon={<BellOffIcon size={26} />}
      title="ยังไม่ได้เปิดรับ notification"
      text="เปิดสิทธิ์รับ notification บนเครื่องนี้ก่อน จึงจะเข้าห้องและส่งข้อความได้"
      action={<button className="btn btn--sm" onClick={() => setTab("status")}>ไปหน้าสถานะ</button>}
    />
  );

  return (
    <div className="app">
      <div className="topbar">
        <header className="appbar">
          <span className="appbar__mark"><BellIcon size={20} /></span>
          <span className="appbar__text">
            <span className="appbar__title">Push Demo</span>
            <span className="appbar__sub">เดโม Web Push บน PWA</span>
          </span>
          <span className="appbar__end">
            <ThemeToggle />
            <span className={`pill ${active ? "pill--success" : "pill--danger"}`}>
              <span className={`dot dot--${active ? "on" : "off"}`} />
              {active ? "พร้อมใช้งาน" : "ยังไม่เปิด"}
            </span>
          </span>
        </header>

        <nav className="tabbar" aria-label="เมนูหลัก">
          {TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              className="tabbar__item"
              aria-current={tab === id ? "page" : undefined}
              onClick={() => setTab(id)}
            >
              <span className="tabbar__icon">
                <Icon size={22} />
                {id === "rooms" && channels.length > 0 && <span className="tabbar__badge">{channels.length}</span>}
              </span>
              {label}
            </button>
          ))}
        </nav>
      </div>

      <main className="main">
        {tab === "status" && (
          <section className="screen" aria-labelledby="screen-status">
            <div className="hero">
              <span className="hero__eyebrow"><SparkIcon size={14} /> Web Push / PWA</span>
              <h1 className="hero__title" id="screen-status">ทดสอบแจ้งเตือนข้ามเครื่อง</h1>
              <p className="hero__text">ติดตั้งเป็นแอปบนหน้าจอโฮม เปิดสิทธิ์รับ notification แล้วเข้าห้องเดียวกันกับเครื่องอื่น ข้อความที่ส่งในห้องจะเด้งเข้าทุกอุปกรณ์ที่อยู่ในห้องนั้น</p>
              {!appInstalled && (
                <button className="btn btn--soft btn--block" onClick={install}>
                  <DownloadIcon size={18} />
                  {installPrompt ? "ติดตั้งเป็นแอป" : "วิธีติดตั้งเป็นแอป"}
                </button>
              )}
            </div>

            <section className="card">
              <div className="card__head">
                <div>
                  <h2 className="card__title">สถานะอุปกรณ์นี้</h2>
                  <p className="card__sub">แต่ละเบราว์เซอร์หรือโปรไฟล์จะมี subscription แยกของตัวเอง</p>
                </div>
                <span className={`pill ${config?.sendEnabled ? "pill--success" : "pill--danger"}`}>
                  {config?.sendEnabled ? "ส่งได้" : "ปิดส่ง"}
                </span>
              </div>

              <div className="stack">
                <div className="tiles">
                  <Tile label="Web Push" value={supported ? "รองรับ" : "ไม่รองรับ"} tone={supported ? "on" : "off"} />
                  <Tile label="โหมดแอป" value={standalone ? "ติดตั้งแล้ว" : "ในเบราว์เซอร์"} tone={standalone ? "on" : "idle"} />
                  <Tile
                    label="สิทธิ์"
                    value={permission === "granted" ? "อนุญาต" : permission === "denied" ? "ปฏิเสธ" : permission === "unsupported" ? "ไม่รองรับ" : "ยังไม่ถาม"}
                    tone={permission === "granted" ? "on" : permission === "default" ? "idle" : "off"}
                  />
                  <Tile label="Subscription" value={active ? "ใช้งานอยู่" : "ยังไม่เปิด"} tone={active ? "on" : "off"} />
                </div>

                {!supported && <Banner tone="danger">เบราว์เซอร์นี้ไม่รองรับ Web Push จึงยังทดสอบไม่ได้ ลองใช้ Chrome, Edge หรือ Safari รุ่นใหม่</Banner>}
                {supported && !standalone && (
                  <Banner>
                    iPhone/iPad ต้องเปิดเว็บจากไอคอนบนหน้าจอโฮมก่อนกดเปิดรับ notification{" "}
                    <button type="button" className="banner__link" onClick={() => setGuideOpen(true)}>ดูวิธีติดตั้ง</button>
                  </Banner>
                )}
                {config && !config.sendEnabled && <Banner tone="warn">การส่งถูกปิดด้วย <code>DEMO_SEND_ENABLED=false</code> เปิดใช้งานหลังตั้งค่า VAPID แล้ว restart service</Banner>}

                {active ? (
                  <button className="btn btn--ghost btn--block" onClick={disableNotifications} disabled={busy !== null}>
                    <BellOffIcon size={18} />
                    {busy === "disable" ? "กำลังปิด..." : "ปิดรับ notification"}
                  </button>
                ) : (
                  <button className="btn btn--block" onClick={enableNotifications} disabled={!supported || busy !== null}>
                    <BellIcon size={18} />
                    {busy === "enable" ? "กำลังเปิด..." : "เปิดรับ notification"}
                  </button>
                )}

                <button className="btn btn--ghost btn--block" onClick={forceUpdate} disabled={busy !== null}>
                  <BoltIcon size={18} />
                  {busy === "update" ? "กำลังอัปเดต..." : "ล้างแคชและอัปเดตแอป"}
                </button>
                <p className="footnote">ใช้เมื่อปิดเปิดแอปแล้วยังเห็นหน้าจอเวอร์ชันเก่า ระบบจะล้างแคชแล้วโหลดใหม่ โดยไม่ยกเลิก subscription</p>
              </div>
            </section>

            <p className="footnote">การที่ push provider รับคำขอสำเร็จ ไม่ได้แปลว่าระบบปฏิบัติการจะแสดงผลเสมอไป หากผู้ใช้ปิดสิทธิ์ ออฟไลน์ หรือเปิด Focus/DND</p>
          </section>
        )}

        {tab === "rooms" && !currentRoom && (
          <section className="screen" aria-labelledby="screen-rooms">
            <div className="screen__head">
              <h1 className="screen__title" id="screen-rooms">ห้องแจ้งเตือน</h1>
              <p className="screen__lede">แตะที่ห้องเพื่อเข้าไปส่งข้อความ ข้อความจะเด้งเข้าทุกอุปกรณ์ที่อยู่ในห้องเดียวกัน</p>
            </div>

            {!active ? <section className="card">{needsSubscription}</section> : (
              <>
                <section className="card">
                  <div className="card__head">
                    <div>
                      <h2 className="card__title">ห้องของเครื่องนี้</h2>
                      <p className="card__sub">ออกจากห้องได้ทุกเมื่อ อุปกรณ์อื่นของคุณจะไม่ได้รับผลกระทบ</p>
                    </div>
                    <span className="pill">{channels.length}/{MAX_CHANNELS_PER_SUBSCRIPTION}</span>
                  </div>

                  {channels.length > 0 ? (
                    <div className="room-list">
                      {channels.map((channel) => (
                        <button className="room" key={channel.id} onClick={() => openRoom(channel)}>
                          <span className="room__icon"><UsersIcon size={20} /></span>
                          <span className="room__body">
                            <span className="room__name">{channel.name}</span>
                            <span className="room__meta">
                              {channel.memberCount} อุปกรณ์ในห้อง
                              {readRoomSecret(channel.id) ? "" : " · ล็อกอยู่"}
                            </span>
                          </span>
                          <span className="room__end"><ChevronRightIcon size={18} /></span>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <EmptyState icon={<UsersIcon size={26} />} title="ยังไม่ได้เข้าห้องไหน" text="สร้างห้องใหม่หรือเข้าห้องเดิมด้วยชื่อห้องกับรหัสห้องเดียวกัน" />
                  )}
                </section>

                <section className="card">
                  <div className="card__head">
                    <div>
                      <h2 className="card__title">เข้าห้อง / สร้างห้อง</h2>
                      <p className="card__sub">ถ้ายังไม่มีห้องชื่อนี้ ระบบจะสร้างให้พร้อมรหัสที่กรอก</p>
                    </div>
                    <button className="btn btn--sm btn--soft" onClick={() => setJoinOpen((current) => !current)}>
                      {joinOpen ? <CloseIcon size={16} /> : <PlusIcon size={16} />}
                      {joinOpen ? "ปิด" : "เพิ่มห้อง"}
                    </button>
                  </div>

                  {joinOpen && (
                    <div className="stack">
                      <div className="field">
                        <span className="field__top"><label className="field__label" htmlFor="room-name">ชื่อห้อง</label></span>
                        <input
                          id="room-name"
                          className="input"
                          value={joinName}
                          onChange={(event) => setJoinName(event.target.value)}
                          placeholder="เช่น sales-demo"
                          maxLength={48}
                          autoComplete="off"
                          autoCapitalize="none"
                          spellCheck={false}
                          enterKeyHint="next"
                        />
                      </div>

                      <SecretField
                        id="room-join-secret"
                        label="รหัสห้อง"
                        hint="ทุกเครื่องที่จะอยู่ห้องเดียวกันต้องใช้ชื่อห้องและรหัสห้องชุดเดียวกัน"
                        value={joinSecret}
                        onChange={setJoinSecret}
                        placeholder="อย่างน้อย 8 ตัวอักษร"
                        enterKeyHint="done"
                      />

                      <button className="btn btn--block" onClick={join} disabled={!joinName || !joinSecret || busy !== null}>
                        <UsersIcon size={18} />
                        {busy === "join" ? "กำลังเข้าห้อง..." : "เข้าห้อง"}
                      </button>
                    </div>
                  )}
                </section>
              </>
            )}
          </section>
        )}

        {tab === "rooms" && currentRoom && (
          <section className="screen" aria-labelledby="screen-room">
            <div className="roomhead">
              <button type="button" className="btn btn--icon" onClick={closeRoom} aria-label="กลับไปรายชื่อห้อง">
                <ChevronLeftIcon size={20} />
              </button>
              <span className="roomhead__text">
                <span className="roomhead__name" id="screen-room">{currentRoom.name}</span>
                <span className="roomhead__meta">ส่งถึง {currentRoom.memberCount} อุปกรณ์ในห้องนี้</span>
              </span>
              <button
                type="button"
                className="btn btn--sm btn--danger"
                onClick={() => leave(currentRoom.id)}
                disabled={busy !== null}
              >
                <ExitIcon size={16} />
                {busy === `leave-${currentRoom.id}` ? "กำลังออก..." : "ออกจากห้อง"}
              </button>
            </div>

            <section className="card">
              {!roomSecret ? (
                <div className="stack">
                  <div className="card__head">
                    <div>
                      <h2 className="card__title">ปลดล็อกห้องก่อนส่ง</h2>
                      <p className="card__sub">ใส่รหัสห้องเพื่อยืนยันตัวตนของผู้ส่ง รหัสจะถูกจำไว้บนเครื่องนี้จนกว่าจะกดล็อกห้องหรือออกจากห้อง</p>
                    </div>
                    <span className="pill"><LockIcon size={14} /> ล็อกอยู่</span>
                  </div>

                  <SecretField
                    id="room-unlock-secret"
                    label="รหัสห้อง"
                    value={unlockSecret}
                    onChange={setUnlockSecret}
                    placeholder="รหัสของห้องนี้"
                    enterKeyHint="go"
                  />

                  <button className="btn btn--block" onClick={() => unlockRoom(currentRoom.id)} disabled={unlockSecret.length < 8}>
                    <LockIcon size={18} />
                    ปลดล็อกห้อง
                  </button>
                </div>
              ) : (
                <div className="stack">
                  <div className="card__head">
                    <div>
                      <h2 className="card__title">ส่งแจ้งเตือนเข้าห้อง</h2>
                      <p className="card__sub">ทุกอุปกรณ์ที่เข้าร่วมห้องนี้จะได้รับ รวมถึงเครื่องนี้ด้วย</p>
                    </div>
                    <button className="btn btn--sm btn--ghost" onClick={() => lockRoom(currentRoom.id)}>ล็อกห้อง</button>
                  </div>

                  <div className="field">
                    <span className="field__top">
                      <label className="field__label" htmlFor="room-title">หัวข้อ</label>
                      <span className="field__count">{customTitle.length}/{MAX_TITLE_LENGTH}</span>
                    </span>
                    <input
                      id="room-title"
                      className="input"
                      value={customTitle}
                      onChange={(event) => setCustomTitle(event.target.value)}
                      maxLength={MAX_TITLE_LENGTH}
                      placeholder="เช่น สถานะคำสั่งซื้ออัปเดตแล้ว"
                      enterKeyHint="next"
                    />
                  </div>

                  <div className="field">
                    <span className="field__top">
                      <label className="field__label" htmlFor="room-body">ข้อความ</label>
                      <span className="field__count">{customBody.length}/{MAX_BODY_LENGTH}</span>
                    </span>
                    <textarea
                      id="room-body"
                      className="textarea"
                      value={customBody}
                      onChange={(event) => setCustomBody(event.target.value)}
                      maxLength={MAX_BODY_LENGTH}
                      placeholder="พิมพ์ข้อความที่ต้องการส่งให้ทุกคนในห้อง"
                    />
                  </div>

                  <button
                    className="btn btn--block"
                    onClick={() => sendRoom(currentRoom.id)}
                    disabled={sendingDisabled || !customTitle.trim() || !customBody.trim() || busy !== null}
                  >
                    <SendIcon size={18} />
                    {busy === "room-send" ? "กำลังส่ง..." : `ส่งถึง ${currentRoom.memberCount} อุปกรณ์`}
                  </button>
                  {!config?.sendEnabled && <Banner tone="warn">การส่งถูกปิดอยู่ในการตั้งค่าเซิร์ฟเวอร์</Banner>}
                </div>
              )}
            </section>

            <section className="card">
              <div className="card__head">
                <div>
                  <h2 className="card__title">อุปกรณ์ในห้องนี้</h2>
                  <p className="card__sub">ชื่ออุปกรณ์มาจาก browser ที่กดเปิดรับ notification ไว้ นำเครื่องอื่นออกจากห้องได้ เครื่องที่ถูกนำออกต้องเข้าห้องใหม่</p>
                </div>
                <button
                  className="btn btn--sm btn--soft"
                  onClick={() => void refreshDevices(currentRoom.id)}
                  disabled={busy !== null}
                >
                  <BoltIcon size={16} />
                  รีเฟรช
                </button>
              </div>

              {devices.length > 0 ? (
                <div className="stack">
                  {!roomSecret && devices.length > 1 && (
                    <Banner>ปลดล็อกห้องด้วยรหัสห้องก่อน จึงจะนำอุปกรณ์เครื่องอื่นออกจากห้องได้</Banner>
                  )}
                  <ul className="device-list">
                    {devices.map((device) => (
                      <li className="device" key={device.id}>
                        <span className="device__icon"><PhoneIcon size={18} /></span>
                        <span className="device__body">
                          <span className="device__name">{device.label}</span>
                          <span className="device__meta">เข้าห้องเมื่อ {formatDateTime(device.joinedAt)}</span>
                        </span>
                        {device.isCurrent ? (
                          <span className="pill pill--success">เครื่องนี้</span>
                        ) : roomSecret ? (
                          <button
                            type="button"
                            className="btn btn--sm btn--danger device__remove"
                            onClick={() => void removeDevice(currentRoom.id, device)}
                            disabled={busy !== null}
                          >
                            <UserMinusIcon size={16} />
                            {busy === `remove-${device.id}` ? "กำลังนำออก..." : "นำออก"}
                          </button>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <EmptyState icon={<PhoneIcon size={26} />} title="ยังโหลดรายชื่ออุปกรณ์ไม่ได้" text="กดรีเฟรชอีกครั้ง หรือกลับเข้าห้องใหม่" />
              )}
            </section>

            <p className="footnote">การกดที่ notification จะเปิดหน้าแรกของเดโมเสมอ อุปกรณ์ที่ปิดสิทธิ์หรือถอนการติดตั้งไปแล้วจะถูกตัดออกจากห้องโดยอัตโนมัติ</p>
          </section>
        )}

        {tab === "presets" && (
          <section className="screen" aria-labelledby="screen-presets">
            <div className="screen__head">
              <h1 className="screen__title" id="screen-presets">ข้อความตัวอย่าง</h1>
              <p className="screen__lede">กดหนึ่งครั้งเพื่อส่งข้อความสำเร็จรูปจากเซิร์ฟเวอร์เข้าเครื่องนี้ทันที เหมาะกับการสาธิตหน้างาน</p>
            </div>

            <section className="card">
              <div className="card__head">
                <div>
                  <h2 className="card__title">ส่งเข้าเครื่องนี้</h2>
                  <p className="card__sub">เนื้อหาข้อความกำหนดไว้ที่เซิร์ฟเวอร์ ผู้ใช้แก้ไขไม่ได้</p>
                </div>
                <span className="pill"><PhoneIcon size={14} /> Self</span>
              </div>

              {!active ? needsSubscription : (
                <div className="preset-list">
                  {PRESETS.map((preset) => (
                    <button
                      className="preset"
                      key={preset.id}
                      onClick={() => void sendSelf(preset.id)}
                      disabled={sendingDisabled || busy !== null}
                    >
                      <span className="preset__icon"><BoltIcon size={20} /></span>
                      <span className="preset__body">
                        <span className="preset__label">{busy === `preset-${preset.id}` ? "กำลังส่ง..." : preset.label}</span>
                        <span className="preset__title">{preset.title}</span>
                      </span>
                      <span className="preset__end"><ChevronRightIcon size={18} /></span>
                    </button>
                  ))}
                </div>
              )}
            </section>
          </section>
        )}
      </main>

      {notice && (
        <div className={`toast toast--${notice.type}`} role="status" aria-live="polite">
          <NoticeIcon size={18} className="toast__icon" />
          <span className="toast__text">{notice.text}</span>
          <button type="button" className="btn btn--icon toast__close" onClick={() => setNotice(null)} aria-label="ปิดข้อความ">
            <CloseIcon size={18} />
          </button>
        </div>
      )}

      <InstallGuide open={guideOpen} onClose={() => setGuideOpen(false)} />
    </div>
  );
}

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};
